import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Clock, Flag, ChevronLeft, ChevronRight, ShieldAlert, AlertTriangle, Maximize2,
} from "lucide-react";
import { toast } from "sonner";
import { LogoMark, BRAND } from "@/components/Logo";
import api from "@/lib/api";

const VIOLATION_LABEL = {
  tab_switch: "You switched away from this tab",
  window_blur: "Window lost focus",
  fullscreen_exit: "You exited fullscreen",
  right_click: "Right-click is disabled during this exam",
  copy: "Copy is disabled during this exam",
  paste: "Paste is disabled during this exam",
  blocked_shortcut: "That keyboard shortcut is disabled during this exam",
};

const BLOCKED_KEYS = new Set(["p", "s", "u", "c", "v", "x", "a"]);

export default function TakeAssessment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState({});
  const [visited, setVisited] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [violations, setViolations] = useState([]);
  const startTime = useRef(Date.now());
  const submittedRef = useRef(false);
  const idxRef = useRef(0);
  // Lazy-init mutable refs to satisfy react-hooks/purity (no fresh literal each render).
  const violationsRef = useRef(null);
  const lastEventAt = useRef(null);
  if (violationsRef.current === null) violationsRef.current = [];
  if (lastEventAt.current === null) lastEventAt.current = {};

  useEffect(() => { idxRef.current = idx; }, [idx]);

  // load
  useEffect(() => {
    api.get(`/assessments/${id}`).then(r => {
      setAssessment(r.data);
      setTimeLeft((r.data.duration || 0) * 60);
      setVisited({ 0: true });
    });
  }, [id]);

  const proctoring = assessment?.proctoring_enabled !== false;
  const threshold = assessment?.violation_threshold || 2;

  const submit = useCallback(async (opts = {}) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    const time_taken = Math.floor((Date.now() - startTime.current) / 1000);
    try {
      const { data } = await api.post(`/assessments/${id}/submit`, {
        answers,
        time_taken,
        violations: violationsRef.current,
        auto_submitted: !!opts.autoSubmitted,
      });
      // release fullscreen before navigating away
      if (document.fullscreenElement) {
        try { await document.exitFullscreen(); } catch (e) { /* ignore */ }
      }
      navigate(`/student/result/${data.id}`, { replace: true });
    } catch (e) {
      toast.error("Failed to submit: " + (e.response?.data?.detail || e.message));
      setSubmitting(false);
      submittedRef.current = false;
    }
  }, [id, answers, navigate]);

  const recordViolation = useCallback((type, detail = "") => {
    if (!proctoring || submittedRef.current) return;
    // 1.5s debounce for the same event type
    const now = Date.now();
    if (lastEventAt.current[type] && now - lastEventAt.current[type] < 1500) return;
    lastEventAt.current[type] = now;

    const v = {
      type,
      detail,
      at: new Date().toISOString(),
      question_index: idxRef.current,
    };
    violationsRef.current = [...violationsRef.current, v];
    setViolations(violationsRef.current);

    const count = violationsRef.current.length;
    const label = VIOLATION_LABEL[type] || type;
    if (count >= threshold) {
      toast.error(`${label} — limit reached, auto-submitting.`);
      submit({ autoSubmitted: true });
    } else {
      toast.warning(`${label} (warning ${count} of ${threshold})`);
    }
  }, [proctoring, threshold, submit]);

  // anti-cheat: enter fullscreen + bind listeners
  useEffect(() => {
    if (!assessment || !proctoring) return;

    // try to enter fullscreen
    document.documentElement.requestFullscreen?.().catch(() => { /* user must allow */ });

    const onVisibility = () => { if (document.hidden) recordViolation("tab_switch"); };
    const onBlur = () => recordViolation("window_blur");
    const onFullscreenChange = () => {
      if (!document.fullscreenElement && !submittedRef.current) {
        recordViolation("fullscreen_exit");
      }
    };
    const onContextMenu = (e) => { e.preventDefault(); recordViolation("right_click"); };
    const onCopy = (e) => { e.preventDefault(); recordViolation("copy"); };
    const onCut = (e) => { e.preventDefault(); recordViolation("copy"); };
    const onPaste = (e) => { e.preventDefault(); recordViolation("paste"); };
    const onKeydown = (e) => {
      const key = (e.key || "").toLowerCase();
      const isDevtools = key === "f12"
        || (e.ctrlKey && e.shiftKey && ["i", "j", "c"].includes(key));
      const isBlockedCombo = e.ctrlKey && BLOCKED_KEYS.has(key);
      if (isDevtools || isBlockedCombo) {
        e.preventDefault();
        recordViolation("blocked_shortcut", e.key);
      }
    };
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "Leaving will submit your exam with current answers.";
      return e.returnValue;
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("contextmenu", onContextMenu);
    document.addEventListener("copy", onCopy);
    document.addEventListener("cut", onCut);
    document.addEventListener("paste", onPaste);
    document.addEventListener("keydown", onKeydown);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("contextmenu", onContextMenu);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("cut", onCut);
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("keydown", onKeydown);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [assessment, proctoring, recordViolation]);

  // timer
  useEffect(() => {
    if (!assessment) return;
    const t = setInterval(() => {
      setTimeLeft(v => {
        if (v <= 1) { clearInterval(t); submit({ autoSubmitted: true }); return 0; }
        return v - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [assessment, submit]);

  if (!assessment) return <div className="min-h-screen grid place-items-center text-slate-500">Loading exam…</div>;
  const qs = assessment.questions || [];
  if (qs.length === 0) return <div className="min-h-screen grid place-items-center">No questions.</div>;
  const q = qs[idx];

  const goto = (i) => { setIdx(i); setVisited(v => ({ ...v, [i]: true })); };
  const select = (val) => setAnswers(a => {
    if (val === "" || val == null) {
      const { [q.id]: _drop, ...rest } = a;
      return rest;
    }
    return { ...a, [q.id]: val };
  });
  const toggleMark = () => setMarked(m => ({ ...m, [idx]: !m[idx] }));

  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;
  const lowTime = timeLeft <= 300;

  const handleSubmit = () => {
    const unanswered = qs.filter(x => answers[x.id] === undefined).length;
    if (unanswered > 0 && !window.confirm(`${unanswered} question(s) unanswered. Submit anyway?`)) return;
    submit();
  };

  const restoreFullscreen = () => {
    document.documentElement.requestFullscreen?.().catch(() => { /* ignore */ });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col select-none">
      {/* Top bar */}
      <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LogoMark size="sm" />
          <div className="leading-tight min-w-0">
            <div className="text-xs uppercase tracking-wider text-slate-500">{BRAND.short}</div>
            <div className="text-sm font-semibold text-slate-900 truncate max-w-[260px]">{assessment.title}</div>
          </div>
        </div>
        <div className="text-sm text-slate-500">Question <span className="font-medium text-slate-900">{idx + 1}</span> of {qs.length}</div>
        <div className="flex items-center gap-3">
          {proctoring && (
            <div
              data-testid="violation-counter"
              className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1.5 ${
                violations.length === 0
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700"
              }`}>
              <ShieldAlert className="w-3.5 h-3.5" />
              {violations.length}/{threshold} warnings
            </div>
          )}
          <div data-testid="exam-timer-display"
            className={`font-mono-acc text-lg tracking-wider font-medium ${lowTime ? "text-red-600" : "text-slate-900"}`}>
            <Clock className="inline-block w-4 h-4 mr-1.5 -mt-0.5" />
            {String(mins).padStart(2,"0")}:{String(secs).padStart(2,"0")}
          </div>
        </div>
      </header>

      {/* Fullscreen-restore banner (visible only if exited) */}
      {proctoring && (
        <FullscreenWatcher onRestore={restoreFullscreen} />
      )}

      <div className="flex-1 grid lg:grid-cols-[1fr_320px] gap-0">
        {/* Question */}
        <main className="p-6 lg:p-10 max-w-3xl">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Question {idx + 1}
            <span className="ml-2 text-blue-700">
              · {q.question_type === "short_answer" ? "Short Answer"
                  : q.question_type === "fill_blank" ? "Fill the Blank"
                  : "Multiple Choice"}
            </span>
          </div>
          <h2 data-testid="exam-question-text" className="mt-2 text-xl md:text-2xl text-slate-900 leading-relaxed">{q.question}</h2>

          {(!q.question_type || q.question_type === "mcq") ? (
            <div className="mt-8 space-y-3">
              {[1,2,3,4].map(i => {
                const isSel = answers[q.id] === i;
                return (
                  <button
                    key={i}
                    data-testid={`question-option-${i}`}
                    onClick={() => select(i)}
                    className={`w-full text-left border rounded-xl p-4 flex items-center gap-4 transition-all ${
                      isSel ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600" : "border-slate-200 hover:border-blue-400"
                    }`}>
                    <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-medium ${isSel?'bg-blue-600 text-white':'bg-slate-100 text-slate-600'}`}>
                      {String.fromCharCode(64 + i)}
                    </span>
                    <span className="text-slate-900">{q[`option${i}`]}</span>
                  </button>
                );
              })}
            </div>
          ) : q.question_type === "short_answer" ? (
            <div className="mt-8">
              <label className="block text-sm font-medium text-slate-700 mb-2">Your answer</label>
              <textarea
                data-testid="question-text-answer"
                rows={3}
                value={answers[q.id] || ""}
                onChange={(e) => select(e.target.value)}
                className="w-full border border-slate-300 rounded-xl p-4 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-900 bg-white"
                placeholder="Type your answer here…"
              />
            </div>
          ) : (
            <div className="mt-8">
              <label className="block text-sm font-medium text-slate-700 mb-2">Fill in the blank</label>
              <input
                data-testid="question-text-answer"
                type="text"
                value={answers[q.id] || ""}
                onChange={(e) => select(e.target.value)}
                className="w-full border border-slate-300 rounded-xl p-4 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-900 bg-white"
                placeholder="Your answer…"
              />
            </div>
          )}

          <div className="mt-8 flex items-center gap-3">
            <button onClick={toggleMark} data-testid="exam-mark-review"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium ${marked[idx] ? "bg-amber-50 border-amber-300 text-amber-800" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
              <Flag className="w-4 h-4" /> {marked[idx] ? "Marked for review" : "Mark for review"}
            </button>
          </div>
        </main>

        {/* Palette */}
        <aside className="border-l border-slate-200 bg-white p-6 hidden lg:block">
          <div className="text-sm font-semibold text-slate-900 mb-3">Question palette</div>
          <div className="grid grid-cols-5 gap-2">
            {qs.map((qq, i) => {
              const ans = answers[qq.id] !== undefined;
              const mk = marked[i];
              const vt = visited[i];
              let cls = "bg-slate-100 text-slate-600 border border-slate-200";
              if (ans) cls = "bg-blue-600 text-white border border-blue-600";
              if (mk) cls = "bg-amber-100 text-amber-800 border border-amber-300";
              if (vt && !ans && !mk) cls = "bg-white text-blue-700 border border-blue-300";
              if (i === idx) cls += " ring-2 ring-blue-500 ring-offset-1";
              return (
                <button key={i} data-testid={`palette-question-${i+1}`} onClick={()=>goto(i)} className={`h-9 rounded text-sm font-medium ${cls}`}>
                  {i+1}
                </button>
              );
            })}
          </div>
          <div className="mt-5 text-xs space-y-2 text-slate-600">
            <Legend swatch="bg-blue-600" label="Answered" />
            <Legend swatch="bg-amber-100 border border-amber-300" label="Marked for review" />
            <Legend swatch="bg-white border border-blue-300" label="Visited, no answer" />
            <Legend swatch="bg-slate-100 border border-slate-200" label="Not visited" />
          </div>

          {proctoring && (
            <div className="mt-6 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5 text-blue-600" /> Proctoring active
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
                Tab-switches, fullscreen exits, copy/paste and right-click are tracked.
                At {threshold} warnings the exam is auto-submitted.
              </p>
            </div>
          )}
        </aside>
      </div>

      {/* Bottom bar */}
      <footer className="sticky bottom-0 h-20 bg-white border-t border-slate-200 px-6 flex items-center justify-between">
        <button data-testid="exam-prev-button" onClick={()=>goto(Math.max(0, idx-1))} disabled={idx===0}
          className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium disabled:opacity-40">
          <ChevronLeft className="w-4 h-4" /> Previous
        </button>
        <div className="flex items-center gap-3">
          <button data-testid="exam-submit-button" onClick={handleSubmit} disabled={submitting}
            className="px-5 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-60">
            {submitting ? "Submitting…" : "Submit assessment"}
          </button>
          <button data-testid="exam-next-button" onClick={()=>goto(Math.min(qs.length-1, idx+1))} disabled={idx===qs.length-1}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium disabled:opacity-40">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </footer>
    </div>
  );
}

function FullscreenWatcher({ onRestore }) {
  const [isFs, setIsFs] = useState(!!document.fullscreenElement);
  useEffect(() => {
    const update = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", update);
    update();
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);
  if (isFs) return null;
  return (
    <div data-testid="fullscreen-restore-banner"
      className="bg-amber-50 border-b border-amber-300 px-6 py-2.5 flex items-center justify-between text-sm text-amber-900">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        You exited fullscreen. Please return to fullscreen to continue uninterrupted.
      </div>
      <button onClick={onRestore} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-md text-xs font-medium hover:bg-amber-700">
        <Maximize2 className="w-3.5 h-3.5" /> Return to fullscreen
      </button>
    </div>
  );
}

function Legend({ swatch, label }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-block w-4 h-4 rounded ${swatch}`} />
      <span>{label}</span>
    </div>
  );
}
